import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { FaFileExcel, FaTrashAlt, FaUpload } from "react-icons/fa";
import { Toast } from "primereact/toast";
import { Panel } from "primereact/panel";
import { AiFillFileText } from "react-icons/ai";
import { BiDetail } from "react-icons/bi";
import * as XLSX from "xlsx";
import { PacmanLoader } from "react-spinners";
import { toast } from "react-toastify";

// Styled-components
const FileButton = styled.button`
  background: #005559;
  color: #fff;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  font-size: 1rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 5px;
  &:hover {
    background: #011617;
  }
`;

const FileCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #ccc;
  padding: 10px;
  margin: 10px 0;
  border-radius: 4px;
`;

const Placeholder = styled.h6`
  color: #aaa;
  text-align: center;
`;

const FileDetails = styled.span`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const RemoveButton = styled(FileButton)`
  background: #d03030;
  &:hover {
    background: #a30000;
  }
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  width: 100%;
`;

function FileUpload({ handleFile, onRemoveFile, isRemoveFile }) {
  const toastBC = React.useRef(null);
  const upload = React.useRef(null);
  const [files, setfiles] = useState("");
  const [loading, setloading] = useState(false);

  const removeFile = () => {
    setfiles("");
    handleFile(null);
    upload.current.value = "";
  };

  const header = () => {
    return (
      <>
        <input
          type="file"
          accept=".xlsx,.xls,.csv "
          style={{ display: "none" }}
          ref={upload}
          onChange={(e) => {
            onFileChange(e);
          }}
        />
        <FileButton
          onClick={(e) => {
            e.preventDefault();
            upload.current.click();
          }}
        >
          <FaUpload />
          Choose File
        </FileButton>
        &nbsp;
      </>
    );
  };

  const onFileChange = async (e) => {
    e.preventDefault();
    setloading(true);
    const file = e?.target?.files[0];

    if (!file && files?.length === 0) {
      toast.warn("No file selected.");
      setloading(false);
      return;
    }

    // Check if the file name length is greater than 50 characters
    if (file?.name?.length > 50) {
      toast.warn("File name must be at most 50 characters long");
      setloading(false);
      return;
    }

    // Allowed file types
    const allowedTypes = [
      "text/csv", // Correct MIME type for CSV
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // XLSX
    ];

    // const fileExtension = file.name.split(".").pop().toLowerCase();

    if (
      allowedTypes.includes(file?.type) ||
      file?.name.endsWith(".csv") ||
      file?.name.endsWith(".xlsx")
    ) {
      const reader = new FileReader();
      reader.onload = function (evt) {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 });

        // Filter out the empty rows
        const nonEmptyRows = data.filter(
          (row) => row.filter((cell) => cell).length > 0
        );

        setfiles({
          file,
          rows: nonEmptyRows.length - 1, // -1 for header row
        });

        setloading(false);
        handleFile(file);
      };
      reader.readAsBinaryString(file);
    } else {
      toast.warn("Please upload a valid CSV or Excel file.");
      setloading(false);
    }
  };

  useEffect(() => {
    if (isRemoveFile) {
      removeFile();
      onRemoveFile();
    }
  }, [isRemoveFile]); // eslint-disable-line

  return (
    <Panel header={header}>
      <Toast ref={toastBC} position="bottom-center" />

      <div className="image-flex ">
        {files.file ? (
          <FileCard key={files.file}>
            <FaFileExcel size={24} color="green" />
            <FileDetails>
              <AiFillFileText color="blue" />
              {files.file?.name}
            </FileDetails>
            <FileDetails>
              <BiDetail color="orange" />
              {"Size :" + (files.file?.size / (1024 * 1024)).toFixed(2) + " MB"}
            </FileDetails>
            <FileDetails>
              <BiDetail color="orange" />
              {"Rows: " + files.rows}
            </FileDetails>
            <RemoveButton onClick={removeFile}>
              <FaTrashAlt />
            </RemoveButton>
          </FileCard>
        ) : (
          <Placeholder>
            {loading ? (
              <LoaderContainer>
                <PacmanLoader color={"#02777c"} size={25} />
              </LoaderContainer>
            ) : (
              "No file selected"
            )}
          </Placeholder>
        )}
      </div>
    </Panel>
  );
}

export default FileUpload;
